import { useTheme } from '@mui/material';
import { BarChart, BarLabel, BarLabelProps } from '@mui/x-charts-pro';
import { useTranslation } from 'react-i18next';
import ConversionByVisitTooltip, {
  ConversionTooltipClickableContext,
  ConversionTooltipRowsContext,
} from './conversion-by-visit-tooltip.component';
import { RetentionVisitViewRow } from './retention.utils';

export type ConversionByVisitChartProps = {
  /** Visit rows (visit 0 baseline already omitted by the BE). */
  data: RetentionVisitViewRow[];
  /**
   * When provided, clicking a bar fires this with its visit row (sc-365239) —
   * used to open the Quick-Create Communication drawer for that cohort. x-charts
   * bars can't host a child button, so the whole bar is the target.
   */
  onBarAction?: (row: RetentionVisitViewRow) => void;
  'data-testid'?: string;
};

// null conversion (no data) plots as a gap — MUI skips a null series value, and
// the bar label already renders nothing for null.
const pctOf = (row: RetentionVisitViewRow) =>
  row.conversionFromLastVisitPct == null
    ? null
    : Math.round(row.conversionFromLastVisitPct * 100);

/**
 * Default bar label re-colored to match its bar (critical bars are red) and
 * bolded. Must be an inline style: the default BarLabel sets `fill` and
 * typography via its styled class, which beats SVG attributes.
 */
export const ColoredBarLabel = (props: BarLabelProps) => (
  <BarLabel {...props} style={{ fill: props.color, fontWeight: 700 }} />
);

/**
 * Conversion-from-last-visit bar chart (story 362073). Critical-window bars
 * are colored via an ordinal colorMap on the band axis; the item tooltip is
 * wired through the chart slots.
 */
const ConversionByVisitChart = ({
  data,
  onBarAction,
  'data-testid': dataTestId = 'Retention.ConversionChart',
}: ConversionByVisitChartProps) => {
  const { t } = useTranslation();
  const theme = useTheme();

  const visitNumbers = data.map(row => row.visitNumber);

  return (
    <ConversionTooltipRowsContext.Provider value={data}>
      <ConversionTooltipClickableContext.Provider value={!!onBarAction}>
        <BarChart
          data-testid={dataTestId}
          height={440}
          hideLegend
          onItemClick={
            onBarAction
              ? (_event, item) => onBarAction(data[item.dataIndex])
              : undefined
          }
          sx={onBarAction ? { cursor: 'pointer' } : undefined}
          series={[
            {
              data: data.map(pctOf),
              barLabel: item => (item.value == null ? '' : `${item.value}%`),
              barLabelPlacement: 'outside',
            },
          ]}
          xAxis={[
            {
              scaleType: 'band',
              data: visitNumbers,
              label: t('page.guestJourney.retention.conversionChart.xAxis'),
              colorMap: {
                type: 'ordinal',
                values: visitNumbers,
                colors: data.map(row =>
                  row.critical
                    ? theme.palette.error.main
                    : theme.palette.primary.dark,
                ),
              },
            },
          ]}
          yAxis={[
            {
              min: 0,
              // Headroom above 100 so an outside label over a 100% bar isn't
              // clipped; ticks still stop at 100.
              max: 108,
              tickInterval: [0, 20, 40, 60, 80, 100],
              label: t('page.guestJourney.retention.conversionChart.yAxis'),
              valueFormatter: (value: number) => `${value}%`,
            },
          ]}
          slots={{
            barLabel: ColoredBarLabel,
            tooltip: ConversionByVisitTooltip,
          }}
        />
      </ConversionTooltipClickableContext.Provider>
    </ConversionTooltipRowsContext.Provider>
  );
};

export default ConversionByVisitChart;
